import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plug, Unplug, Download, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useHoldedIntegracion } from '@/hooks/useHoldedIntegracion';
import { HoldedImportDialog } from '@/components/clientes/HoldedImportDialog';

export function HoldedIntegracionSection() {
  const { activa, integracion, cargando, refrescar } = useHoldedIntegracion();
  const [apiKey, setApiKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  // supabase.functions.invoke marca como error cualquier respuesta no-2xx y NO
  // parsea el body. Hay que leer error.context.json() para obtener el detalle.
  const extractErrorMessage = async (error: any, data: any): Promise<string | null> => {
    if (data && (data as any).error) return (data as any).error as string;
    if (!error) return null;
    try {
      const ctx = error.context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) return body.error as string;
      } else if (ctx && typeof ctx.text === 'function') {
        const txt = await ctx.text();
        if (txt) return txt;
      }
    } catch (_) { /* fallthrough */ }
    return error.message || 'Error desconocido';
  };

  const conectar = async () => {
    if (!apiKey.trim()) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('holded-save-credentials', {
        body: { apiKey: apiKey.trim() },
      });
      const msg = await extractErrorMessage(error, data);
      if (msg) throw new Error(msg);
      toast.success('Holded conectado correctamente');
      setApiKey('');
      refrescar();
    } catch (e: any) {
      toast.error(e.message || 'No se pudo conectar con Holded', { duration: 12000 });
    } finally {
      setBusy(false);
    }
  };

  const desconectar = async () => {
    if (!confirm('¿Desconectar Holded? Los vínculos con clientes se mantendrán por si reactivas.')) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('holded-save-credentials', {
        body: { action: 'disconnect' },
      });
      const msg = await extractErrorMessage(error, data);
      if (msg) throw new Error(msg);
      toast.success('Holded desconectado');
      refrescar();
    } catch (e: any) {
      toast.error(e.message || 'Error al desconectar');
    } finally {
      setBusy(false);
    }
  };

  if (cargando) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5" /> Holded
            </CardTitle>
            <CardDescription>
              Sincroniza clientes con tu cuenta de Holded de forma bidireccional.
            </CardDescription>
          </div>
          {activa ? (
            <Badge className="bg-green-600">Conectado</Badge>
          ) : (
            <Badge variant="outline">No conectado</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!activa ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="holded-api-key">API Key de Holded</Label>
              <Input
                id="holded-api-key"
                type="password"
                placeholder="Pega aquí tu API Key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Puedes usar una clave v2 (<code>pat_</code>/<code>sk_</code>) con permisos <code>contacts:contacts.read</code> y <code>contacts:contacts.write</code>, o una API Key clásica v1 sin prefijo desde Holded → <strong>Settings → Developers</strong>.
              </p>
            </div>
            <Button onClick={conectar} disabled={busy || !apiKey.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plug className="h-4 w-4 mr-2" />}
              Conectar
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {integracion?.ultima_importacion && (
              <p className="text-sm text-muted-foreground">
                Última importación: {new Date(integracion.ultima_importacion).toLocaleString('es-ES')}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setImportOpen(true)} variant="default">
                <Download className="h-4 w-4 mr-2" /> Importar clientes de Holded
              </Button>
              <Button
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data, error } = await supabase.functions.invoke('holded-reclassify-contacts');
                    const msg = await extractErrorMessage(error, data);
                    if (msg) throw new Error(msg);
                    const d: any = data;
                    toast.success(
                      `Reclasificados: ${d.actualizados} · Clientes ${d.distribucion?.cliente ?? 0} · Proveedores ${d.distribucion?.proveedor ?? 0} · Ambos ${d.distribucion?.ambos ?? 0}`,
                      { duration: 10000 },
                    );
                  } catch (e: any) {
                    toast.error(e.message || 'Error al reclasificar', { duration: 12000 });
                  } finally { setBusy(false); }
                }}
                variant="secondary"
                disabled={busy}
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Reclasificar clientes/proveedores
              </Button>
              <Button onClick={desconectar} variant="outline" disabled={busy}>
                <Unplug className="h-4 w-4 mr-2" /> Desconectar
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              «Reclasificar» consulta Holded y actualiza el tipo (cliente/proveedor/ambos) de todos los contactos ya importados sin duplicar nada.
            </p>
          </div>
        )}
      </CardContent>
      <HoldedImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </Card>
  );
}
